/**
 * Proof-of-work bot check — shared, dependency-free core.
 *
 * This module is imported by BOTH the backend verifier (`common/pow.ts`) and
 * the web frontend (`utils/botCheck.ts`), the same cross-boundary pattern the
 * codebase already uses for `common/publicPages.ts`. It must therefore stay
 * free of any `db` / `@mindstudio-ai/agent` import and of any DOM/Node global —
 * only constants and pure functions live here.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE CHECK IS
 * ---------------------------------------------------------------------------
 * The anonymous front door (/try, /spark) calls paid AI models with no account.
 * The visitor-id / history caps (guestTurnCap, sparkCap) stay as the free-
 * preview conversion nudge, but they are forgeable. This is the floor beneath
 * them: before each anonymous AI call the browser must solve a small proof of
 * work — find a nonce so that SHA-256("rand.ts.nonce") starts with
 * `POW_DIFFICULTY_BITS` zero bits. Real visitors solve it invisibly in a
 * background worker in a fraction of a second; a script hammering the endpoint
 * pays real CPU for every single call instead of rotating a free UUID. The
 * backend re-checks the proof (cheap) and refuses anything that doesn't meet
 * the difficulty, is stale, or has been seen before.
 *
 * No third party, no account, no secret to manage. The security property is
 * "one fresh solve per call," enforced by difficulty + freshness + a spent-
 * proof table on the backend.
 */

/**
 * Required number of leading zero BITS in the SHA-256 of the token. Expected
 * work to find a solution is ~2^BITS hashes. 18 → ~260k hashes, well under a
 * second in a worker on desktop and ~1-2s on a slow phone, which is why the
 * frontend solves ahead of time and keeps a small pool ready.
 *
 * Single source of truth: the backend enforces this exact value, and the
 * frontend hands it to its solver worker, so the two can never drift.
 */
export const POW_DIFFICULTY_BITS = 18;

/**
 * How recent a proof's embedded timestamp must be. The timestamp is part of the
 * hashed preimage, so it cannot be changed without redoing the work — which is
 * what stops an attacker from precomputing a large stock of proofs far in
 * advance. It also bounds how long the spent-proof table must remember a proof.
 */
export const POW_FRESHNESS_MS = 5 * 60 * 1000;

/**
 * Prefix on the thrown message when a proof is missing or invalid. The /try and
 * /spark frontends match on this to transparently solve a fresh proof and retry
 * once, so a real visitor never sees it.
 */
export const BOT_CHECK_PREFIX = '[BOT_CHECK]';

/**
 * Count leading zero bits of a hash byte array. Pure; identical logic runs in
 * the browser solver and the backend verifier so they always agree.
 */
export function leadingZeroBits(bytes: Uint8Array): number {
  let bits = 0;
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b === 0) {
      bits += 8;
      continue;
    }
    let mask = 0x80;
    while (mask > 0 && (b & mask) === 0) {
      bits++;
      mask >>= 1;
    }
    break;
  }
  return bits;
}
